export type UserRole = string;

export interface Role {
  _id?: string;
  id?: string;
  name: string;
  label: string;
  color: string;
  nav: string[];
  canView: boolean;
  canEdit: boolean;
  canApprove: boolean;
  canDelete: boolean;
  template?: string;
  permissions?: {
    module: string;
    canView: boolean;
    canCreate?: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canApprove: boolean;
    canExport?: boolean;
  }[];
  reportPermissions?: {
    category: string;
    canView: boolean;
    canExport: boolean;
    canSchedule: boolean;
    canCustomBuild: boolean;
  }[];
}

export interface Holiday {
  _id?: string;
  id?: string;
  name: string;
  date: string;
  type?: string;
}

export interface User {
  _id?: string;
  username?: string;
  email: string;
  password?: string;
  name?: string;
  companyId?: string;
  company?: string;
  role: UserRole;
  dept?: string;
  initials?: string;
  empId?: string;               // human-readable e.g. "E001"
  employeeMongoId?: string;     // MongoDB ObjectId of Employee doc
  avColor?: string;
  permissions?: {
    module: string;
    canView: boolean;
    canCreate?: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canApprove: boolean;
    canExport?: boolean;
    type?: "grant" | "revoke";
    reason?: string;
    expiry?: string;
  }[];
  extraPermissions?: string[];
  roleData?: any;
}

export interface Employee {
  id: string;
  _id?: string;
  user?: any;
  name: string;
  dept: string;
  role: string;
  sysRole: string;
  status: string;
  joined: string;
  shift?: string;
  salaryStructure?: {
    grossSalary: number;
    earnings: { name: string; amount: number }[];
    deductions: { name: string; amount: number }[];
  };
  email: string;
  initials: string;
  avColor: string;
  profilePic?: string;
  phone?: string;
  address?: string;
  emergencyContact?: string;
  manager?: string;
  bankDetails?: {
    bankName?: string;
    accountNumber?: string;
    ifscCode?: string;
    branchName?: string;
  };
  aadhaar?: string;
  pan?: string;
  esic?: string;
  pfAcc?: string;
  uan?: string;
  openingLeaveBalance?: {
    casual: number;
    sick: number;
    annual: number;
    floating: number;
    monthly: number;
  };
  permissions?: any[];
}

export interface LeaveRequest {
  id: string;
  empId: string;
  name: string;
  type: string;
  duration?: string;
  dates: string;
  startDate: string;
  endDate: string;
  days: number;
  status: string;
  initials: string;
  avColor: string;
  reason: string;
  currentApprover?: any;
  approvalChain?: { approver: any; action: string; comment?: string; date: string }[];
}

export interface AttendanceRecord {
  id: string;
  empId: string;
  sysId?: string;
  name: string;
  date: string;
  clockIn: string;
  clockOut: string;
  workHrs: string;
  breakHrs: string;
  status: string;
  initials: string;
  avColor: string;
}

export interface PayrollRecord {
  id: string;
  empId: string;
  name: string;
  dept: string;
  earnings?: { name: string; amount: number }[];
  deductions?: { name: string; amount: number }[];
  grossEarnings?: number;
  totalDeductions?: number;
  net: number;
  month: string;
  status: string;
  paymentDate?: string;
  paidDays?: number;
}

export interface Job {
  id: string;
  title: string;
  dept: string;
  level: string;
  location: string;
  posted: string;
  applicants: number;
  status: string;
  stage: string;
}

export interface Candidate {
  id: string;
  _id?: string;
  companyId?: string;
  jobId: string | { _id: string; id?: string; title?: string; dept?: string };
  name: string;
  role: string;
  stage: string;
  score: number;
  applied: string;
  source?: "LinkedIn" | "Referral" | "Website" | "Agency" | "Other";
  interviewDate?: string;
  interviewer?: string;
  interviewMode?: "Google Meet" | "Zoom" | "In-office" | "Phone";
  round?: string;
  email?: string;
  phone?: string;
  cvUrl?: string;
  cvData?: any;
  cvOriginalName?: string;
  cvMimeType?: string;
  updatedAt?: string;
}

export interface PerformanceRecord {
  id: string;
  empId: string;
  name: string;
  dept: string;
  score: number;
  rating: number;
  goals: string;
  initials: string;
  avColor: string;
  quarter: string;
}

export interface TrainingProgram {
  id: string;
  name: string;
  category: string;
  enrolled: number;
  completed: number;
  deadline: string;
  instructor: string;
  duration: string;
  status?: string;
  enrolledEmployees?: any[];
  totalModules?: number;
}

export interface Department {
  id: string;
  name: string;
  head: string;
  employees: number;
  avgSalary: number;
  budgetUsed: number;
  openRoles: number;
color: string;
  description?: string;
  status?: string;
  location?: string;
  icon?: string;
}

export interface Designation {
  id: string;
  _id?: string;
  name: string;
  department: Department | string;
  description?: string;
  status?: string;
}

export interface OnboardingTask {
  department: "HR" | "IT" | "Manager";
  task: string;
  done: boolean;
}

export type OnboardingStage = 'offer' | 'documentation' | 'itsetup' | 'orientation' | 'handover' | 'fullyOnboarded';

export interface OnboardingEmployee {
  id: string;
  _id?: string;
  name: string;
  role: string;
  dept: string;
  joinDate: string;
  buddy?: string;
  progress: number;
  stage: OnboardingStage;
  tasks: { task: string; done: boolean }[];
}

export interface OnboardingConfig {
  id?: string;
  hrChecklist: { task: string; done: boolean }[];
  schedule: { time: string; title: string; sub: string }[];
}

export interface Notification {
  id: string;
  type: string;
  message: string;
  time: string;
  read: boolean;
}

export interface TimesheetRecord {
  _id?: string;
  id?: string;
  employee?: any;
  project?: any;
  startDate: string;
  endDate: string;
  description: string;
  tasks: { date?: string; taskName: string; time: number }[];
  status: string;
  manager?: any;
  comments?: string;
}

export interface Shift {
  _id?: string;
  id?: string;
  code: string;
  name: string;
  type: "General" | "Night" | "Rotational" | "Flexible";
  startTime: string;
  endTime: string;
  breakDuration: number;
  graceMin: number | null;
  offDays: string[];
  colorTag: string;
  otEnabled: boolean;
  otMultiplier: string;
  otAppliesAfter: string;
  assignedCount: number;
  assignedUsers: { userId: string; name: string; initials: string; color: string }[];
  status: "Active" | "Inactive";
}

export interface RosterEntry {
  _id?: string;
  id?: string;
  employeeId?: string;
  name: string;
  initials: string;
  color: string;
  schedule: string[];
}

export interface RoleConfig {
  label: string;
  color: string;
  nav: string[];
  canApproveLeave: boolean;
  canEditEmployee: boolean;
  canViewSalary: boolean;
  canProcessPayroll: boolean;
  canViewAllAtt: boolean;
  canPostJob: boolean;
  showClockPanel: boolean;
}

export const ROLES_CONFIG: Record<string, RoleConfig> = {
  admin: { label: 'Admin', color: 'blue', nav: ['dashboard', 'chat', 'employees', 'attendance', 'leave', 'timesheets', 'payroll', 'recruitment', 'onboarding', 'performance', 'training', 'departments', 'reports', 'settings'], canApproveLeave: true, canEditEmployee: true, canViewSalary: true, canProcessPayroll: true, canViewAllAtt: true, canPostJob: true, showClockPanel: true },
  'master admin': { label: 'Super Admin', color: 'blue', nav: ['dashboard', 'chat', 'employees', 'attendance', 'leave', 'timesheets', 'payroll', 'recruitment', 'onboarding', 'performance', 'training', 'departments', 'reports', 'settings', 'companies', 'mycompany'], canApproveLeave: true, canEditEmployee: true, canViewSalary: true, canProcessPayroll: true, canViewAllAtt: true, canPostJob: true, showClockPanel: true },
  'super admin': { label: 'Super Admin', color: 'blue', nav: ['dashboard', 'chat', 'employees', 'attendance', 'leave', 'timesheets', 'payroll', 'recruitment', 'onboarding', 'performance', 'training', 'departments', 'reports', 'settings', 'companies', 'mycompany'], canApproveLeave: true, canEditEmployee: true, canViewSalary: true, canProcessPayroll: true, canViewAllAtt: true, canPostJob: true, showClockPanel: true },
  hr: { label: 'HR Manager', color: 'teal', nav: ['dashboard', 'chat', 'employees', 'attendance', 'leave', 'timesheets', 'payroll', 'recruitment', 'onboarding', 'performance', 'training', 'departments', 'reports'], canApproveLeave: true, canEditEmployee: true, canViewSalary: true, canProcessPayroll: true, canViewAllAtt: true, canPostJob: true, showClockPanel: true },
  manager: { label: 'Manager', color: 'purple', nav: ['dashboard', 'chat', 'employees', 'attendance', 'leave', 'timesheets', 'performance', 'training'], canApproveLeave: true, canEditEmployee: false, canViewSalary: false, canProcessPayroll: false, canViewAllAtt: true, canPostJob: false, showClockPanel: true },
  employee: { label: 'Employee', color: 'amber', nav: ['dashboard', 'chat', 'attendance', 'leave', 'timesheets', 'performance', 'training'], canApproveLeave: false, canEditEmployee: false, canViewSalary: false, canProcessPayroll: false, canViewAllAtt: false, canPostJob: false, showClockPanel: true },
  finance: { label: 'Finance', color: 'green', nav: ['dashboard', 'chat', 'timesheets', 'payroll', 'reports'], canApproveLeave: false, canEditEmployee: false, canViewSalary: true, canProcessPayroll: true, canViewAllAtt: false, canPostJob: false, showClockPanel: true },
  recruiter: { label: 'Recruiter', color: 'rose', nav: ['dashboard', 'chat', 'recruitment', 'onboarding'], canApproveLeave: false, canEditEmployee: false, canViewSalary: false, canProcessPayroll: false, canViewAllAtt: false, canPostJob: true, showClockPanel: true },
};

export const AVATAR_COLORS: Record<string, string> = {
  blue: 'linear-gradient(135deg,#B6F2D1,#85D1DB)',
  purple: 'linear-gradient(135deg,#85D1DB,#B3EBF2)',
  teal: 'linear-gradient(135deg,#B3EBF2,#B6F2D1)',
  green: 'linear-gradient(135deg,#22c55e,#16a34a)',
  amber: 'linear-gradient(135deg,#f59e0b,#ef4444)',
  rose: 'linear-gradient(135deg,#f43f5e,#f59e0b)',
};

export const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  'Active': { bg: '#dcfce7', text: '#16a34a' },
  'On Leave': { bg: '#fef3c7', text: '#d97706' },
  'Inactive': { bg: '#fee2e2', text: '#dc2626' },
  'Approved': { bg: '#dcfce7', text: '#16a34a' },
  'Rejected': { bg: '#fee2e2', text: '#dc2626' },
  'Pending': { bg: '#fef3c7', text: '#d97706' },
  'Paid': { bg: '#dcfce7', text: '#16a34a' },
  'Processing': { bg: '#C9FDF2', text: '#6BB8C3' },
  'On Time': { bg: '#dcfce7', text: '#16a34a' },
  'Late': { bg: '#fef3c7', text: '#d97706' },
  'Absent': { bg: '#fee2e2', text: '#dc2626' },
  'WFH': { bg: '#ccfbf1', text: '#0d9488' },
  'Engineering': { bg: '#C9FDF2', text: '#6BB8C3' },
  'Sales': { bg: 'rgba(133,209,219,0.12)', text: '#6BB8C3' },
  'Marketing': { bg: '#ccfbf1', text: '#0d9488' },
  'Finance': { bg: '#dcfce7', text: '#16a34a' },
  'HR': { bg: '#fef3c7', text: '#d97706' },
  'Operations': { bg: '#ffe4e6', text: '#e11d48' },
};
